import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Typography, Chip, Collapse, IconButton } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { SERVER_NAME, genericTokenIcon } from './config';

const ACTION_TYPE_STYLE = {
  PAPER_BUY:  { color: '#9ef7c5', bg: 'rgba(158,247,197,0.12)' },
  PAPER_SELL: { color: '#ff4d6a', bg: 'rgba(255,77,106,0.12)'  },
  PAPER_EXIT: { color: '#ff4d6a', bg: 'rgba(255,77,106,0.12)'  },
  REJECT:     { color: '#9ea4d8', bg: 'rgba(158,164,216,0.10)' },
  WAIT:       { color: '#ffe55c', bg: 'rgba(255,229,92,0.10)'  },
};

const VERDICT_COLOR = {
  validated:   '#9ef7c5',
  invalidated: '#ff4d6a',
  recorded:    '#9ea4d8',
};

function fmt2(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(2) : '—';
}
function fmtUsd(v) {
  const n = Number(v);
  return Number.isFinite(n) && n !== 0 ? `$${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}` : '—';
}
function fmtPct(v) {
  const n = Number(v);
  return Number.isFinite(n) ? `${n >= 0 ? '+' : ''}${n.toFixed(2)}%` : '—';
}
function fmtDate(s) {
  if (!s) return '—';
  try { return new Date(s).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' }); }
  catch { return String(s); }
}
function shortId(s) {
  if (!s || s.length < 12) return s || '—';
  return `${s.slice(0, 8)}…${s.slice(-4)}`;
}
function resolveIconValue(raw) {
  const iconRaw = raw != null ? String(raw).trim() : '';
  const iconLower = iconRaw.toLowerCase ? iconRaw.toLowerCase() : iconRaw;
  if (!iconRaw || iconLower === 'null' || iconLower === 'undefined' || iconLower === '""') return '';
  if (iconRaw.startsWith('{')) {
    try {
      const parsed = JSON.parse(iconRaw);
      return parsed && parsed.image ? parsed.image : '';
    } catch (e) {
      return '';
    }
  }
  return iconRaw;
}
function normalizeTokenIconCandidate(raw) {
  const icon = resolveIconValue(raw);
  if (!icon) return '';
  if (icon === genericTokenIcon) return '';
  return icon;
}
function pickTokenIcon(row) {
  if (!row) return '';
  return normalizeTokenIconCandidate(row.icon)
    || normalizeTokenIconCandidate(row.icon2)
    || normalizeTokenIconCandidate(row.logo)
    || normalizeTokenIconCandidate(row.image)
    || normalizeTokenIconCandidate(row.originalIcon);
}
const useStyles = makeStyles(() => ({
  card: {
    borderRadius: 12,
    border: '1px solid rgba(77, 121, 255, 0.22)',
    background: 'rgba(10, 14, 30, 0.92)',
    overflow: 'hidden',
    marginBottom: 10,
  },
  header: {
    padding: '10px 14px',
    background: 'linear-gradient(90deg, rgba(77,121,255,0.18), rgba(10,14,30,0.96))',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    cursor: 'pointer',
    userSelect: 'none',
  },
  chips: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    flexWrap: 'wrap',
  },
  meta: {
    fontSize: 11,
    color: '#9ea4d8',
  },
  tokenIcon: {
    width: 20,
    height: 20,
    borderRadius: '50%',
    border: '1px solid rgba(255,255,255,0.34)',
    boxShadow: '0 0 0 2px rgba(77,121,255,0.14)',
    flex: '0 0 auto',
    objectFit: 'cover',
    background: 'rgba(255,255,255,0.08)',
  },
  body: {
    padding: '10px 14px 12px',
  },
  thesis: {
    fontSize: 12,
    lineHeight: 1.55,
    color: '#e8f5ff',
    opacity: 0.92,
    marginBottom: 8,
  },
  rejectReason: {
    fontSize: 12,
    lineHeight: 1.5,
    color: '#ffb347',
    background: 'rgba(255,179,71,0.08)',
    border: '1px solid rgba(255,179,71,0.25)',
    borderRadius: 6,
    padding: '6px 10px',
    marginBottom: 8,
  },
  statGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))',
    gap: 6,
    marginTop: 6,
  },
  statBox: {
    background: 'rgba(255,255,255,0.04)',
    borderRadius: 8,
    padding: '6px 10px',
    border: '1px solid rgba(255,255,255,0.07)',
  },
  statLabel: {
    fontSize: 10,
    color: '#9ea4d8',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    marginBottom: 2,
  },
  statValue: {
    fontSize: 13,
    fontWeight: 700,
    color: '#e5e7ff',
  },
  outcomeBox: {
    marginTop: 10,
    padding: '8px 10px',
    borderRadius: 8,
    border: '1px solid rgba(77,121,255,0.22)',
    background: 'rgba(5,8,20,0.6)',
  },
  outcomeTitle: {
    fontSize: 10,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    fontWeight: 900,
    color: '#a78bfa',
    marginBottom: 6,
  },
}));

function OutcomesPanel({ outcomes }) {
  const classes = useStyles();
  if (!outcomes || !outcomes.length) return null;
  return (
    <div className={classes.outcomeBox}>
      <Typography className={classes.outcomeTitle}>Outcomes</Typography>
      {outcomes.map((o, i) => (
        <div key={i} className={classes.statGrid} style={{ marginBottom: i < outcomes.length - 1 ? 8 : 0 }}>
          <div className={classes.statBox}>
            <div className={classes.statLabel}>Type</div>
            <div className={classes.statValue} style={{ fontSize: 11 }}>{o.outcome_type || '—'}</div>
          </div>
          <div className={classes.statBox}>
            <div className={classes.statLabel}>Verdict</div>
            <div className={classes.statValue} style={{ color: VERDICT_COLOR[o.verdict] || '#e5e7ff' }}>
              {o.verdict || '—'}
            </div>
          </div>
          <div className={classes.statBox}>
            <div className={classes.statLabel}>P&L</div>
            <div className={classes.statValue} style={{ color: Number(o.pnl_usd) >= 0 ? '#9ef7c5' : '#ff4d6a' }}>
              {fmtUsd(o.pnl_usd)}
            </div>
          </div>
          <div className={classes.statBox}>
            <div className={classes.statLabel}>P&L %</div>
            <div className={classes.statValue} style={{ color: Number(o.pnl_pct) >= 0 ? '#9ef7c5' : '#ff4d6a' }}>
              {fmtPct(o.pnl_pct)}
            </div>
          </div>
          {Number(o.holding_days) > 0 ? (
            <div className={classes.statBox}>
              <div className={classes.statLabel}>Held</div>
              <div className={classes.statValue}>{`${Number(o.holding_days).toFixed(1)}d`}</div>
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
}

export default function AgentVerdictCard({ verdict, outcomes }) {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(false);
  const [tokenIcon, setTokenIcon] = useState(verdict.icon || verdict.token_icon || verdict.icon_url || '');

  const atStyle = ACTION_TYPE_STYLE[verdict.action_type] || { color: '#cfd8ff', bg: 'rgba(207,216,255,0.08)' };
  const confidence = Number(verdict.confidence_score);

  useEffect(() => {
    let cancelled = false;
    const tokenAddress = verdict.token_address ? String(verdict.token_address).trim() : '';
    const tokenSymbol = verdict.symbol ? String(verdict.symbol).trim() : '';
    const existingIcon = verdict.icon || verdict.token_icon || verdict.icon_url;
    const normalizedExistingIcon = normalizeTokenIconCandidate(existingIcon);
    if (normalizedExistingIcon) {
      setTokenIcon(normalizedExistingIcon);
      return undefined;
    }
    if (!tokenAddress && !tokenSymbol) {
      setTokenIcon('');
      return undefined;
    }

    const findMatchingRow = (rows) => {
      const normalizedAddress = tokenAddress.toLowerCase();
      const normalizedSymbol = tokenSymbol.toLowerCase();
      return rows.find(row => normalizedAddress && String(row.id || row.address || '').toLowerCase() === normalizedAddress)
        || rows.find(row => normalizedSymbol && String(row.symbol || '').toLowerCase() === normalizedSymbol)
        || rows[0];
    };

    const fetchJson = (url) => fetch(url).then(r => r.ok ? r.json() : null).catch(() => null);
    const priceSearch = tokenAddress || tokenSymbol;
    const chain = verdict.chain ? String(verdict.chain).trim().toUpperCase() : 'ETH';
    const metadataUrl = tokenAddress
      ? `${SERVER_NAME}/tokens/${encodeURIComponent(tokenAddress)}/metadata?chain=${encodeURIComponent(chain)}`
      : '';
    const priceUrl = `${SERVER_NAME}/fetchTokenPricesWithHistoryAllRanges?dataSource=1&offset=0&limit=10&search=${encodeURIComponent(priceSearch)}`;
    const dbUrl = `${SERVER_NAME}/fetchTokensDB?dataSource=ETHERSCAN&search=${encodeURIComponent(priceSearch)}`;

    const resolveFromMetadata = metadataUrl
      ? fetchJson(metadataUrl).then((meta) => {
          if (cancelled) return '';
          const metaIcon = normalizeTokenIconCandidate(meta && (meta.icon || meta.icon2 || meta.image || meta.logo));
          return metaIcon || '';
        })
      : Promise.resolve('');

    resolveFromMetadata
      .then(priceData => {
        if (cancelled) return '';
        if (priceData) return priceData;
        return fetchJson(priceUrl).then((data) => {
          if (cancelled) return '';
          const priceRows = data && Array.isArray(data.rows) ? data.rows : (Array.isArray(data) ? data : []);
          const priceIcon = pickTokenIcon(findMatchingRow(priceRows));
          if (priceIcon) return priceIcon;
          return fetchJson(dbUrl).then(dbData => {
            const dbRows = Array.isArray(dbData) ? dbData : [];
            return pickTokenIcon(findMatchingRow(dbRows));
          });
        });
      })
      .then(icon => {
        if (cancelled) return;
        setTokenIcon(normalizeTokenIconCandidate(icon) || '');
      })
      .catch(() => {
        if (!cancelled) setTokenIcon('');
      });

    return () => { cancelled = true; };
  }, [verdict.action_id, verdict.icon, verdict.icon_url, verdict.symbol, verdict.token_address, verdict.token_icon]);

  return (
    <div className={classes.card}>
      <div
        className={classes.header}
        onClick={() => setExpanded((v) => !v)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setExpanded((v) => !v); }}
      >
        <div className={classes.chips}>
          <Chip
            size="small"
            label={verdict.action_type || '—'}
            style={{
              height: 18, fontSize: 9, fontWeight: 900, textTransform: 'uppercase',
              color: atStyle.color, backgroundColor: atStyle.bg,
              border: `1px solid ${atStyle.color}`,
            }}
          />
          <img
            className={classes.tokenIcon}
            src={tokenIcon || genericTokenIcon}
            alt=""
            onError={(e) => {
              if (e.currentTarget.src !== genericTokenIcon) e.currentTarget.src = genericTokenIcon;
            }}
          />
          <Typography className={classes.meta} style={{ fontWeight: 700, color: '#e5e7ff' }}>
            {verdict.symbol || verdict.token_address?.slice(0, 8) || '—'}
          </Typography>
          {verdict.simulated_side && verdict.simulated_side !== 'none' ? (
            <Typography className={classes.meta}>{verdict.simulated_side}</Typography>
          ) : null}
          {Number.isFinite(confidence) ? (
            <Typography className={classes.meta}>{`conf ${confidence.toFixed(2)}`}</Typography>
          ) : null}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Typography className={classes.meta}>{fmtDate(verdict.created_at)}</Typography>
          <IconButton size="small" style={{ padding: 2, color: '#cfd3ff' }}>
            {expanded ? <ExpandMoreIcon style={{ fontSize: 16 }} /> : <ChevronRightIcon style={{ fontSize: 16 }} />}
          </IconButton>
        </div>
      </div>

      <Collapse in={expanded}>
        <div className={classes.body}>
          {verdict.thesis_summary ? (
            <Typography className={classes.thesis}>{verdict.thesis_summary}</Typography>
          ) : null}
          {verdict.reason_summary ? (
            <Typography className={classes.thesis} style={{ opacity: 0.75 }}>{verdict.reason_summary}</Typography>
          ) : null}
          {verdict.reject_reason ? (
            <Typography className={classes.rejectReason}>{verdict.reject_reason}</Typography>
          ) : null}
          <div className={classes.statGrid}>
            <div className={classes.statBox}>
              <div className={classes.statLabel}>Entry Price</div>
              <div className={classes.statValue}>{fmtUsd(verdict.entry_price)}</div>
            </div>
            <div className={classes.statBox}>
              <div className={classes.statLabel}>Allocation</div>
              <div className={classes.statValue}>{fmtUsd(verdict.allocation_usd)}</div>
            </div>
            <div className={classes.statBox}>
              <div className={classes.statLabel}>Risk Score</div>
              <div className={classes.statValue}>{fmt2(verdict.risk_score)}</div>
            </div>
            <div className={classes.statBox}>
              <div className={classes.statLabel}>Liquidity</div>
              <div className={classes.statValue}>{fmtUsd(verdict.liquidity_usd)}</div>
            </div>
            {verdict.market_regime ? (
              <div className={classes.statBox}>
                <div className={classes.statLabel}>Regime</div>
                <div className={classes.statValue} style={{ fontSize: 11 }}>{verdict.market_regime}</div>
              </div>
            ) : null}
            {verdict.trade_id ? (
              <div className={classes.statBox}>
                <div className={classes.statLabel}>Trade ID</div>
                <div className={classes.statValue} style={{ fontSize: 11 }}>{shortId(verdict.trade_id)}</div>
              </div>
            ) : null}
          </div>
          <OutcomesPanel outcomes={outcomes} />
        </div>
      </Collapse>
    </div>
  );
}
